import type { Match } from '@hooks/use-search-by-bounding-box';
import { GridColumn } from '@tkww/orion-web-ds';
import { useAtom, useAtomValue } from 'jotai';
import React, { type FunctionComponent, useMemo } from 'react';
import { connect } from 'react-redux';
import { MapViewButton } from '../../../MapView';
import { appliedFiltersCountAtom } from '../../atoms';
import PillMore from '../../components/PillMore';
import { filterConfigsSidebarAtom } from '../../components/Sidebar/atoms';
import { ClearFiltersLink } from '../AppliedFilters/components/ClearFiltersLink';
import FilterPillCategory from './filterPillCategory';
import Styles from './styles.scss';
import * as utils from './utils';

export type StateProps = ReturnType<typeof mapStateToProps>;

export interface OwnProps {
	isSticky: boolean;
	/**
	 * The condensed search bar, rendered inside the pill row's flex container so
	 * it sits inline with the pills. It has to
	 * live in here rather than alongside this component — the pills are wrapped
	 * in a full-width `GridColumn xs="12"`, so a sibling of that column has no
	 * horizontal room and wraps onto its own line.
	 */
	condensedSearch?: React.ReactNode;
	/**
	 * Mobile web: the "Search" pill that opens the mobile search modal. Rendered inside the same flex container as the pills
	 * so it sits directly after "Filters", matching the `[Filters] [Search]`
	 * sticky bar in the mWeb design. Mobile's counterpart to `condensedSearch` —
	 * the two are mutually exclusive by breakpoint.
	 */
	mobileSearchPill?: React.ReactNode;
}

interface MapViewButtonProps {
	match: Match;
	searchLocation: string;
	showMapView: boolean;
}

type Props = StateProps & OwnProps & MapViewButtonProps;

interface FilterPillLayoutProps {
	filterCategories: string[];
	isMobile: boolean;
	showMapView: boolean;
	isSticky: boolean;
}

const FilterPillLayout = ({
	filterCategories,
	isMobile,
	showMapView,
	isSticky,
}: FilterPillLayoutProps) => {
	const [appliedFiltersCount] = useAtom(appliedFiltersCountAtom);
	const sidebarConfigs = useAtomValue(filterConfigsSidebarAtom);
	const pills = filterCategories.map((filterCategory) => (
		<FilterPillCategory key={filterCategory} filterCategory={filterCategory} />
	));
	// Only show "More Filters" when the sidebar has renderable filters not already visible as pills.
	const hasExtraFilters = useMemo(
		() =>
			sidebarConfigs.some((config) => !filterCategories.includes(config.name)),
		[sidebarConfigs, filterCategories],
	);
	return (
		<>
			{pills}
			{hasExtraFilters && <PillMore showMapView={showMapView} />}
			{appliedFiltersCount > 0 && isSticky && !isMobile && <ClearFiltersLink />}
		</>
	);
};

const FilterPills: FunctionComponent<Props> = (props) => {
	const {
		filterCategories,
		isMobile,
		sidebarCategories,
		isSticky,
		match,
		searchLocation,
		showMapView,
		condensedSearch,
		mobileSearchPill,
	} = props;

	return (
		<GridColumn xs="12">
			<div
				className={utils.createClassName(isSticky, showMapView)}
				data-map-view={showMapView}
				// Drives the "only the search bar absorbs the shrinking" rule in
				// styles.scss — see .condensed-search.
				data-condensed-search={Boolean(condensedSearch)}
			>
				<FilterPillLayout
					isMobile={isMobile}
					isSticky={isSticky}
					filterCategories={filterCategories}
					showMapView={showMapView}
				/>
				{/*
				 * Directly after FilterPillLayout so it lands immediately after
				 * "Filters" (the mobile form of PillMore, which that layout renders
				 * last), giving the design's `[Filters] [Search]` order. Needs no
				 * wrapper — Pill is already a flex item, so it picks up the same
				 * inter-pill spacing as its neighbours.
				 */}
				{mobileSearchPill}
				{showMapView && (
					<MapViewButton match={match} searchLocation={searchLocation} />
				)}
				{condensedSearch && (
					<div className={Styles.condensedSearch}>{condensedSearch}</div>
				)}
			</div>
		</GridColumn>
	);
};

const mapStateToProps = (state: Redux.State) => {
	return {
		filterCategories: state.search.filterPills.categories,
		isMobile: state.viewport.isMobile,
		sidebarCategories: state.search.filterPills.sidebar.categories,
	};
};

export default connect<StateProps, OwnProps>(mapStateToProps)(FilterPills);
